import { Col, Drawer, Input, Row } from 'antd';
import { observer } from 'mobx-react-lite';
import React, { useContext, useRef, useState } from 'react';
import { Link, NavLink, useHistory } from 'react-router-dom';
import { RootStoresContext } from '../../stores/RootStore';

import { ReactComponent as IconCloseMenu } from '../../icons/icon-close.svg';
import { ReactComponent as IconLogo } from '../../icons/icon-logo.svg';
import { ReactComponent as IconMenu } from '../../icons/icon-menu.svg';
import { ReactComponent as IconSearch } from '../../icons/icon-search.svg';
import { cleanString } from '../../util/renderHelpers';

export interface InavigationProps {
  children?: never;
  readonly name: string;
  readonly url: string;
  closeMobileMenu: any;
}

const NavigationItem: React.FC<InavigationProps> = ({ name, url, closeMobileMenu }) => {
  return (
    <li className="nav__item">
      <NavLink
        exact={true}
        className="nav__link"
        to={url}
        onClick={closeMobileMenu}
        title={cleanString(name)}
      >
        {cleanString(name)}
      </NavLink>
    </li>
  );
};

const NavigationItemLink: React.FC<InavigationProps> = ({ name, url, closeMobileMenu }) => {
  return (
    <li className="nav__item">
      <a
        className="nav__link"
        href={url}
        title={cleanString(name)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={closeMobileMenu}
      >
        {cleanString(name)}
      </a>
    </li>
  );
};

const Navigation: React.FC = observer(() => {
  const rootStore = useContext(RootStoresContext);
  const closeMobileMenu = rootStore.appStore.isMobile ? rootStore.appStore.closeMobileMenu : null;
  const displayNavigationItems =
    rootStore.siteStore.getMenuNav &&
    rootStore.siteStore.getMenuNav.map(el =>
      el.linkUrl ? (
        <NavigationItemLink
          key={`menu-${el.assetId}`}
          name={el.shortName}
          url={el.linkUrl}
          closeMobileMenu={closeMobileMenu}
        />
      ) : (
        <NavigationItem
          key={`menu-${el.assetId}`}
          name={el.shortName}
          url={el.urlPath}
          closeMobileMenu={closeMobileMenu}
        />
      )
    );

  return (
    rootStore.siteStore.getMenuNav && (
      <nav className="header__nav">
        <ul className="nav__list">
          <NavigationItem
            name="Home"
            url={`${process.env.REACT_APP_BASE_PATH}` || ''}
            closeMobileMenu={closeMobileMenu}
          />
          {displayNavigationItems}
        </ul>
      </nav>
    )
  );
});

const HeaderLogo: React.FC = observer(() => {
  const rootStore = useContext(RootStoresContext);
  return (
    <div className="header__logo">
      <div className="logo__wrapper">
        {rootStore.siteStore.siteLogo ? (
          <img className="logo" alt="logo" src={rootStore.siteStore.siteLogo} />
        ) : (
          <IconLogo />
        )}
      </div>
    </div>
  );
});

const HeaderTitle: React.FC = () => {
  const rootStore = useContext(RootStoresContext);
  return (
    <div className="header__title">
      <h1 className="header__tagline">{cleanString(rootStore.siteStore.siteName)}</h1>
    </div>
  );
};

const HeaderSearch: React.FC = () => {
  const history = useHistory();
  const [value, setValue] = useState('');

  const inputHandler = (el: React.ChangeEvent<HTMLInputElement>) => setValue(el.target.value);

  const searchHandler = (searchValue: string) => {
    history.push(`${process.env.REACT_APP_BASE_PATH || ''}/search?query=${searchValue}`);
    setValue('');
  };

  return (
    <div className="header__search">
      <Input.Search
        autoComplete="off"
        spellCheck={false}
        className="search__input"
        placeholder="Search for keyword"
        name="Search Input"
        aria-label="Search Input"
        onChange={inputHandler}
        value={value}
        onSearch={searchHandler}
        enterButton={
          <button aria-label="Search" className="search-toggle" title="Search">
            <IconSearch />
          </button>
        }
      />
    </div>
  );
};

const HeaderDesktop: React.FC = () => {
  return (
    <>
      <Link className="header__link" to={process.env.REACT_APP_BASE_PATH || ''} title="Home">
        <HeaderLogo />
        <HeaderTitle />
      </Link>
      <Navigation />
      <HeaderSearch />
    </>
  );
};

const HeaderMobileMenu: React.FC = observer(() => {
  const rootStore = useContext(RootStoresContext);
  return (
    <div className="mobile__menu">
      <button
        aria-label="Toggle Menu"
        className="header__mobile-toggle"
        onClick={rootStore.appStore.openMobileMenu}
      >
        <IconMenu />
      </button>
      <Drawer
        placement="top"
        closable={false}
        onClose={rootStore.appStore.closeMobileMenu}
        visible={rootStore.appStore.isMobileMenuOpen}
        height="top"
        className="mobile-menu__drawer"
      >
        <button
          aria-label="Close Menu"
          className="header__mobile-close"
          onClick={rootStore.appStore.closeMobileMenu}
        >
          <IconCloseMenu />
        </button>
        <Navigation />
      </Drawer>
    </div>
  );
});

const HeaderMobileSearch: React.FC = observer(() => {
  const rootStore = useContext(RootStoresContext);
  const history = useHistory();
  const [value, setValue] = useState('');

  const inputRef: any = useRef(null);
  const setFocus = (visible: boolean) => visible && inputRef.current && inputRef.current.focus();

  const inputHandler = (el: React.ChangeEvent<HTMLInputElement>) => setValue(el.target.value);

  const searchHandler = (searchValue: string) => {
    history.push(`${process.env.REACT_APP_BASE_PATH || ''}/search?query=${searchValue}`);
    rootStore.appStore.closeMobileSearch();
    setValue('');
  };

  return (
    <div className="mobile__search">
      <button
        aria-label="Search"
        className="search-toggle"
        onClick={rootStore.appStore.openMobileSearch}
      >
        <IconSearch />
      </button>
      <Drawer
        placement="top"
        closable={false}
        onClose={rootStore.appStore.closeMobileSearch}
        visible={rootStore.appStore.isMobileSearchOpen}
        height="top"
        className="mobile-menu__drawer"
        afterVisibleChange={setFocus}
      >
        <button
          aria-label="Close Menu"
          className="header__mobile-close"
          onClick={rootStore.appStore.closeMobileSearch}
        >
          <IconCloseMenu />
        </button>
        <Input.Search
          autoComplete="off"
          spellCheck={false}
          className="search__input"
          placeholder="Search for keyword"
          name="Mobile Search Input"
          aria-label="Mobile Search Input"
          ref={inputRef}
          value={value}
          onChange={inputHandler}
          onSearch={searchHandler}
          enterButton={
            <button aria-label="Search" className="search-toggle">
              <IconSearch />
            </button>
          }
        />
      </Drawer>
    </div>
  );
});

const HeaderMobile: React.FC = () => {
  return (
    <>
      <HeaderMobileMenu />
      <div className="header__mobile">
        <Link className="header__link" to={process.env.REACT_APP_BASE_PATH || ''} title="Home">
          <HeaderLogo />
          <HeaderTitle />
        </Link>
      </div>
      <HeaderMobileSearch />
    </>
  );
};

const Header: React.FC = observer(() => {
  const rootStore = useContext(RootStoresContext);
  return (
    <header id="header" className="header">
      <Row className="fixed" type="flex">
        <Col className="header__wrapper" xs={{ span: 24 }}>
          {rootStore.appStore.isMobile ? <HeaderMobile /> : <HeaderDesktop />}
        </Col>
      </Row>
    </header>
  );
});

export default React.memo(Header);
